import React, { FC, ReactNode } from 'react'
import { RedirectLoginOptions } from '@auth0/auth0-react'

import { Layout, PrimaryButton } from '@components'
import { ReactComponent as LogoT01A } from './assets/logo_t01a.svg'
import {
  ClaimErrorDetails,
  ClaimMessageKey,
  ClaimErrorAction,
  getErrorAction
} from './claim-errors'
import model from './model.en.json'
import styles from './index.module.scss'

export { model }

const DEFAULT_SUPPORT_EMAIL = 'support@endeavorotc.com'

export const getSupportEmail = (): string =>
  process.env.REACT_APP_ENDEAVOROTC_SUPPORT || DEFAULT_SUPPORT_EMAIL

export const getSupportLabel = (): string =>
  process.env.REACT_APP_ENDEAVOROTC_SUPPORT_UI || getSupportEmail()

/*
  Fills {{name}} placeholders. {{support}} becomes a mailto link; everything
  else is plain text (no HTML injection from API values like practice names).
*/
export const fillCopy = (
  template: string,
  vars: { [name: string]: string | null | undefined } = {}
): ReactNode[] => {
  const parts = template.split(/(\{\{\w+\}\})/g)

  return parts
    .filter((part) => part !== '')
    .map((part, i) => {
      const match = part.match(/^\{\{(\w+)\}\}$/)
      if (!match) {
        return part
      }

      const name = match[1]
      if (name === 'support') {
        return (
          <a key={i} href={`mailto:${getSupportEmail()}`}>
            {getSupportLabel()}
          </a>
        )
      }

      return vars[name] ?? ''
    })
}

export const fillText = (
  template: string,
  vars: { [name: string]: string | null | undefined } = {}
): string => template.replace(/\{\{(\w+)\}\}/g, (_, name) => vars[name] ?? '')

// e.g. "2026-11-08T15:02:00Z" -> "Nov 8, 2026"
export const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return ''
  }

  const date = new Date(iso)
  if (isNaN(date.getTime())) {
    return ''
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

export const claimPath = (token: string) => `/claim/${token}`
export const claimDonePath = (token: string) => `/claim/${token}/done`

/*
  Production: Auth0's redirect_uri is always /payment; the Auth0Provider runs
  onRedirectCallback(appState) before it renders as authenticated, and that
  navigates to appState.returnTo, so the patient lands back on the claim page.
  Local-auth mode reads the same appState.returnTo / screen_hint.
*/
export const buildLoginOptions = (
  returnTo: string,
  screenHint: 'login' | 'signup',
  forceAccountChoice = false
): RedirectLoginOptions => ({
  appState: { returnTo },
  authorizationParams: {
    screen_hint: screenHint,
    // The Akili Universal Login page reads initial_screen (see views/Login).
    initial_screen: screenHint,
    ...(forceAccountChoice ? { prompt: 'login' as const } : {})
  }
})

type ClaimLayoutProps = {
  practiceName?: string | null
  children: JSX.Element
}

export const ClaimLayout: FC<ClaimLayoutProps> = ({
  practiceName,
  children
}) => {
  const leftContent = (
    <div className={styles.brand_panel}>
      <LogoT01A className={styles.brand_logo} aria-label="EndeavorOTC" />
      <h1 className={styles.brand_title}>{model.brand.title}</h1>
      {practiceName && (
        <p className={styles.brand_subtitle}>
          {fillText(model.brand.providedBy, { practice: practiceName })}
        </p>
      )}
    </div>
  )

  return (
    <Layout hasFooter context="claim" leftContent={leftContent}>
      <div className={styles.content}>
        <LogoT01A className={styles.mobile_logo} aria-hidden="true" />
        {children}
      </div>
    </Layout>
  )
}

type ClaimMessageProps = {
  messageKey: ClaimMessageKey
  practiceName?: string | null
  details?: ClaimErrorDetails
  actionInProgress?: boolean
  onAction?: (action: Exclude<ClaimErrorAction, null>) => void
}

const DAY_MS = 24 * 60 * 60 * 1000

/*
  access_active: the link can wait for the current access to end, unless it
  expires first. Expiring within a day of the access ending counts as first,
  so the copy never says "open it again after Dec 22 … works until Dec 22".
*/
const messageBody = (
  messageKey: ClaimMessageKey,
  details?: ClaimErrorDetails
): string => {
  const copy = model.errors[messageKey] as { body: string }
  if (messageKey !== 'access_active') {
    return copy.body
  }

  const { activeUntil, linkExpiresAt } = details || {}
  const expiresFirst =
    !!activeUntil &&
    !!linkExpiresAt &&
    new Date(linkExpiresAt).getTime() < new Date(activeUntil).getTime() + DAY_MS
  return expiresFirst
    ? model.errors.access_active.bodyLinkExpiresFirst
    : model.errors.access_active.body
}

export const ClaimMessage: FC<ClaimMessageProps> = ({
  messageKey,
  practiceName,
  details,
  actionInProgress,
  onAction
}) => {
  const copy = model.errors[messageKey] as {
    title: string
    action?: string
  }
  const action = getErrorAction(messageKey)

  return (
    <div className={styles.message} data-testid={`claim-message-${messageKey}`}>
      <h2 className={styles.title}>{copy.title}</h2>
      <p className={styles.body}>
        {fillCopy(messageBody(messageKey, details), {
          practice: practiceName || model.fallbackPractice,
          activeUntil: formatDate(details?.activeUntil),
          linkExpires: formatDate(details?.linkExpiresAt)
        })}
      </p>
      {action && copy.action && onAction && (
        <div className={styles.actions}>
          <PrimaryButton
            context="claim-message-action"
            className={styles.button}
            caption={copy.action}
            isLoading={actionInProgress}
            onClick={() => onAction(action)}
          />
        </div>
      )}
    </div>
  )
}
